import { api } from "./client";

// Типове устройства, поддържани от бекенда
export type DeviceType = "fiscal" | "pos" | "paymentTerminal";

// Линк между устройство и payment method (бутон/слот за това устройство)
export interface DevicePaymentMethod {
  paymentMethodId: number;
  value: string; // слот от принтера или сентинелът NO_FISCAL_DEVICE_VALUE
}

// Сентинел стойност за device payment method: методът е конфигуриран, но НЕ
// се фискализира — BE-то закрива по POS-only пътя (POS принтер ако има, иначе
// нищо не се печата). Трябва да съвпада с NO_FISCAL_DEVICE_VALUE в BE
// (app/features/payment_methods/service.py).
export const NO_FISCAL_DEVICE_VALUE = "БЕЗ ФИСКАЛИЗИРАНЕ";

// Слот от фискалния драйвер — използва се за конфигурация на стойност (slot)
// за всеки device payment method.
export interface DevicePaymentTypeSlot {
  id: number;
  slot: string;
  fixed: boolean;
  default_name: string;
  name: string;
  configured: boolean;
}

export interface DevicePaymentTypes {
  read_at: string;
  slots: DevicePaymentTypeSlot[];
}

// Едно FiscalAPI, което се е обявило, че обслужва GUID-а на устройството.
// Пълни се само от presence съобщенията по MQTT — не се праща при запис.
export interface ConnectedClient {
  client_id: string; // стабилната идентичност на инсталацията
  client_name: string | null; // човешкото име ("Каса бар")
  hostname: string | null;
  app_version: string | null;
  device_name: string | null; // как се казва устройството в локалното FiscalAPI
  // Обявяват се от FiscalAPI 1.61.0+ — по-стара инсталация ги връща null.
  device_model?: string | null; // моделът зад GUID-а ("datecs_fp700mx")
  // Може ли апаратът да преиздаде бон (копие от КЛЕН / дубликат). null =
  // „не знаем", не „не може" — BE-то блокира само при изричен false.
  supports_receipt_copy?: boolean | null;
  // Чете и сверява часа на апарата по MQTT (FiscalAPI 2.8.9+) — без него
  // „Час" в реда го няма, а BE не праща командата.
  supports_clock_sync?: boolean | null;
  announced_at: string | null; // ISO 8601 — база за "остаряло"
}

// Интерфейс за устройство, върнат от GET /devices, POST /devices, PUT /devices/:id
export interface Device {
  id: number;
  guid: string;
  name: string;
  type: DeviceType;
  userIds: number[];
  stationIds: number[];
  // Език на печат за POS принтер (код от org DB `languages`); null → български.
  printLanguage?: string | null;
  // „Обобщени касови бележки" — само за фискален; липсва при по-старо BE.
  summarizeReceipt?: boolean;
  // „Нефискално копие на ПОС" — само за фискален; липсва при по-старо BE.
  nonFiscalCopy?: boolean;
  // „Печат от ПОС принтер" — само за pos (бележка при трите клика в брой);
  // липсва при по-старо BE → включено.
  printCashNoReceipt?: boolean;
  paymentMethods: DevicePaymentMethod[];
  paymentTypes?: DevicePaymentTypes | null;
  // Липсва при по-старо BE — затова е опционално. Празен масив НЕ значи
  // "няма закачено FiscalAPI", а "няма достатъчно ново FiscalAPI, което да
  // се обявява"; UI-ът трябва да го показва като неизвестно, не като грешка.
  connectedClients?: ConnectedClient[];
  createdAt: string;
  updatedAt: string;
}

// Интерфейс за създаване на устройство — POST /devices
export interface CreateDeviceRequest {
  guid: string;
  name: string;
  type: DeviceType;
  userIds: number[];
  stationIds?: number[];
  // Език на печат — праща се само за pos; null/пропуснат → български.
  printLanguage?: string | null;
  summarizeReceipt?: boolean;
  nonFiscalCopy?: boolean;
  printCashNoReceipt?: boolean;
  paymentMethods?: DevicePaymentMethod[];
}

// Интерфейс за редактиране на устройство — PUT /devices/:id
// Replace стратегия: пращаш пълния списък, бекендът трие старите редове.
export interface UpdateDeviceRequest {
  guid: string;
  name: string;
  type: DeviceType;
  userIds: number[];
  stationIds?: number[];
  printLanguage?: string | null;
  summarizeReceipt?: boolean;
  nonFiscalCopy?: boolean;
  printCashNoReceipt?: boolean;
  paymentMethods?: DevicePaymentMethod[];
}


// Извличане на всички устройства — GET /devices
export const getDevices = async (): Promise<Device[]> => {
  const response = await api.get<Device[]>("/devices");
  return response.data;
};

// Има ли заведението изобщо POS принтер (независимо от потребителя) —
// GET /devices/pos/exists. Само автентикация, без manage_devices.
export const getPosPrintersExist = async (): Promise<boolean> => {
  const response = await api.get<{ exists: boolean }>("/devices/pos/exists");
  return response.data.exists;
};

// Създаване на ново устройство — POST /devices
export const createDevice = async (
  data: CreateDeviceRequest
): Promise<Device> => {
  const response = await api.post<Device>("/devices", data);
  return response.data;
};

// Редактиране на устройство — PUT /devices/:id
export const updateDevice = async (
  deviceId: number,
  data: UpdateDeviceRequest
): Promise<Device> => {
  const response = await api.put<Device>(`/devices/${deviceId}`, data);
  return response.data;
};

// Изтриване на устройство — DELETE /devices/:id
export const deleteDevice = async (deviceId: number): Promise<void> => {
  await api.delete(`/devices/${deviceId}`);
};

// Последната публикувана FiscalAPI версия — GET /devices/fiscalapi/latest.
// BE-то проксира манифеста на update сървъра (кеш 5 мин); url е директният
// линк за сваляне на инсталатора.
export interface FiscalApiLatest {
  version: string | null;
  url: string | null;
  released_at?: string | null;
}

export const getFiscalApiLatest = async (): Promise<FiscalApiLatest> => {
  const response = await api.get<FiscalApiLatest>("/devices/fiscalapi/latest");
  return response.data;
};

// Ack от отдалечения ъпдейт trigger — POST /devices/:id/fiscalapi/update.
// „success" значи само „инсталацията тръгна"; самият ъпдейт тече минути и
// завършва с рестарт на гейтуея — новата версия се появява в
// connectedClients при следващото presence обявяване.
export interface FiscalApiUpdateResult {
  status: string; // success | failed | timeout
  error?: string | null;
  result?: { status?: string; installed_version?: string } | null;
}

// Часът на фискалното устройство (BE 0.0.394 ↔ FiscalAPI 2.8.9) —
// POST /devices/:id/clock/read и /clock/sync. Пликът е като при ъпдейта:
// FE-то чете status/error, не HTTP кода. Часовете са стенният час на обекта
// без зона (`2026-10-26T21:03:15`), както го пази апаратът.
export interface DeviceClockReading {
  device_time_iso?: string | null;
  reference_time_iso?: string | null;
  drift_seconds?: number | null; // + = апаратът е напред
  reference_verified?: boolean;
}

export interface DeviceClockSyncResult {
  // false = отказ ПРЕДИ команда — апаратът не е пипнат.
  synced?: boolean;
  verified?: boolean;
  drift_seconds?: number | null; // разликата ПРЕДИ сверяването
  refusal?: "clock_backward_blocked" | "clock_reference_unverified" | string;
  message?: string; // текстът на касата
  possible_after?: string; // при „назад": последният бон по часовника на апарата
  warning?: string;
}

export interface DeviceClockEnvelope<T> {
  status: string; // success | failed | timeout
  error?: string | null;
  result?: T | null;
}

export const readDeviceClock = async (
  deviceId: number
): Promise<DeviceClockEnvelope<DeviceClockReading>> => {
  const response = await api.post<DeviceClockEnvelope<DeviceClockReading>>(
    `/devices/${deviceId}/clock/read`
  );
  return response.data;
};

export const syncDeviceClock = async (
  deviceId: number
): Promise<DeviceClockEnvelope<DeviceClockSyncResult>> => {
  const response = await api.post<DeviceClockEnvelope<DeviceClockSyncResult>>(
    `/devices/${deviceId}/clock/sync`
  );
  return response.data;
};

export const triggerFiscalApiUpdate = async (
  deviceId: number
): Promise<FiscalApiUpdateResult> => {
  const response = await api.post<FiscalApiUpdateResult>(
    `/devices/${deviceId}/fiscalapi/update`
  );
  return response.data;
};
